import { useEffect } from "react";
import { Field, TextInput } from "./Field";

const BUREAUS = [
    {
        id: "experian",
        name: "Experian",
        url: "https://www.experian.com/consumer-products/free-credit-report.html",
        color: "#4F8EE8",
        extended: true,
    },
    {
        id: "equifax",
        name: "Equifax",
        url: "https://my.equifax.com/consumer-registration",
        color: "#E13B3B",
        extended: false,
    },
    {
        id: "transunion",
        name: "TransUnion",
        url: "https://service.transunion.com/dss/orderStep1_form.page",
        color: "#2BB673",
        extended: false,
    },
];

export function validateBureauLogin() {
    // No required fields — every field on this step is optional.
    return {};
}

function RadioRow({ checked, onClick, label, testId, color }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className="w-full text-left flex items-start gap-3 group py-1"
            data-testid={testId}
        >
            <span
                className={`radio-chip mt-0.5 ${checked ? "checked" : ""}`}
                style={checked && color ? { borderColor: color } : undefined}
            />
            <span className="text-sm" style={{ color: checked ? "#D4AF37" : "rgba(255,255,255,0.85)" }}>
                {label}
            </span>
        </button>
    );
}

function BureauSection({ bureau, value, onChange }) {
    const v = value || {};
    const set = (k, val) => onChange({ ...v, [k]: val });
    return (
        <section className="space-y-4 pb-7 border-b border-[#D4AF37]/10 last:border-0" data-testid={`bureau-${bureau.id}`}>
            <a
                href={bureau.url}
                target="_blank"
                rel="noopener noreferrer"
                className="font-display text-2xl underline underline-offset-4 hover:opacity-80 transition"
                style={{ color: bureau.color, textDecorationColor: bureau.color }}
                data-testid={`bureau-link-${bureau.id}`}
            >
                {bureau.name}
            </a>

            <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Username" testId={`field-username-${bureau.id}`}>
                        <TextInput
                            value={v.username}
                            onChange={(x) => set("username", x)}
                            placeholder="Username"
                            testId={`input-username-${bureau.id}`}
                            autoComplete="off"
                        />
                    </Field>
                    <Field label="Password" testId={`field-password-${bureau.id}`}>
                        <TextInput
                            type="password"
                            value={v.password}
                            onChange={(x) => set("password", x)}
                            placeholder="Password"
                            testId={`input-password-${bureau.id}`}
                            autoComplete="off"
                        />
                    </Field>
                </div>

                {bureau.extended && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Security Question" testId={`field-sq-${bureau.id}`}>
                                <TextInput
                                    value={v.security_question}
                                    onChange={(x) => set("security_question", x)}
                                    placeholder="Security question"
                                    testId={`input-sq-${bureau.id}`}
                                />
                            </Field>
                            <Field label="Security Answer" testId={`field-sa-${bureau.id}`}>
                                <TextInput
                                    value={v.security_answer}
                                    onChange={(x) => set("security_answer", x)}
                                    placeholder="Security answer"
                                    testId={`input-sa-${bureau.id}`}
                                />
                            </Field>
                        </div>
                        <Field label="4-Digit PIN" testId={`field-pin-${bureau.id}`}>
                            <TextInput
                                value={v.pin}
                                onChange={(x) => set("pin", (x || "").replace(/\D/g, "").slice(0, 4))}
                                placeholder="••••"
                                testId={`input-pin-${bureau.id}`}
                                inputMode="numeric"
                                maxLength={4}
                            />
                        </Field>
                        <RadioRow
                            checked={!!v.dont_remember}
                            onClick={() => set("dont_remember", !v.dont_remember)}
                            label="I don't remember my security questions"
                            testId={`bureau-forgot-${bureau.id}`}
                            color={bureau.color}
                        />
                    </>
                )}
            </div>
        </section>
    );
}

export default function StepBureauLogin({ value, onChange, defaultUsername }) {
    const v = value || {};
    const set = (k, val) => onChange({ ...v, [k]: val });

    // Auto-fill Equifax + TransUnion username with the dedicated email from Step 4
    // when the applicant hasn't typed one yet. Runs whenever the source changes.
    useEffect(() => {
        if (!defaultUsername) return;
        const patch = {};
        if (!v.equifax?.username) patch.equifax = { ...(v.equifax || {}), username: defaultUsername };
        if (!v.transunion?.username) patch.transunion = { ...(v.transunion || {}), username: defaultUsername };
        if (Object.keys(patch).length) onChange({ ...v, ...patch });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultUsername]);

    return (
        <div className="space-y-7" data-testid="step-bureau-login">
            <div>
                <div className="step-eyebrow">STEP 5</div>
                <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">Credit Bureau Login Access</h2>
                <p className="mt-3 text-sm text-white/70 leading-relaxed">
                    For each bureau, enter your login details. Select each bureau name to open the link and set up your free account.
                </p>
            </div>

            <div className="space-y-7">
                {BUREAUS.map((b) => (
                    <BureauSection
                        key={b.id}
                        bureau={b}
                        value={v[b.id]}
                        onChange={(val) => set(b.id, val)}
                    />
                ))}
            </div>

            <p className="text-sm text-white/65 leading-relaxed pt-2">
                After submitting your login details, you will receive verification codes from each credit bureau within the next 24 hours. This is a one-time setup.
            </p>
        </div>
    );
}
